import * as yup from "yup";

import { LoadingButton } from "@mui/lab";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { Grid, Typography } from "@mui/material";
import InputNumber from "@/components/hookForms/inputNumber";
import serviceClienteInstalacao from "@/services/serviceClienteInstalacao";
import messages from "@/utils/messages";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    regraDataVencimentoState: any;
}

const dayRangeMessage = "O dia deve estar entre 1 e 31";

export default function DialogRegraDataVencimento({ open, onClose, onSuccess, regraDataVencimentoState }: IProps) {
    const [isLoading, setLoading] = useState(false);

    const validationSchema = yup.object().shape({
        dataFixa: yup
            .string()
            .required(messages.requiredField)
            .test("is-valid-day", dayRangeMessage, (value) => {
                if (!value) {
                    return false;
                }
                const numberValue = Number(value);
                return Number.isInteger(numberValue) && numberValue >= 1 && numberValue <= 31;
            }),
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            dataFixa: "",
        },
    });

    const onSubmit = (values: any) => {
        setLoading(true);

        serviceClienteInstalacao
            .update({
                ...regraDataVencimentoState,
                dataFixa: Number(values.dataFixa),
            })
            .then((result) => {
                if (result) {
                    toast.success(messages.updateSucess);
                    onSuccess();
                }
            })
            .catch((err) => {
                toast.error(messages.updateError, err);
            })
            .finally(() => {
                setLoading(false);
                handleClose();
            });
    };

    const handleClose = () => {
        onClose();
    };

    useEffect(() => {
        if (!open) {
            reset({ dataFixa: "" });
        }
    }, [open, reset]);

    return (
        <Dialog
            maxWidth="md"
            fullWidth
            open={open}
            onClose={(_, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
            aria-labelledby="dialog-regra-data-vencimento-title"
        >
            <DialogTitle id="dialog-regra-data-vencimento-title">Alterar regra de vencimento</DialogTitle>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogContent dividers>
                    <DialogContentText component="div" sx={{ mb: 2 }}>
                        <Typography variant="body1">
                            Você está prestes a alterar a regra de vencimento desta instalação. Essa ação impactará a
                            próxima data de vencimento do cliente.
                        </Typography>
                        <Typography variant="body1">Tem certeza que deseja continuar?</Typography>
                    </DialogContentText>
                    <Grid container spacing={2}>
                        <Grid item md={12} xs={12}>
                            <InputNumber
                                id="dataFixa"
                                name="dataFixa"
                                label="Dia de vencimento (1 a 31)"
                                control={control}
                                error={errors}
                                decimalScale={0}
                                maxLength={2}
                                allowNegative={false}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions style={{ display: "flex", justifyContent: "center", margin: "24px" }}>
                    <Button
                        id="cancelar"
                        onClick={handleClose}
                        color="warning"
                        variant="contained"
                        style={{ width: "159px", height: "44px" }}
                    >
                        Não
                    </Button>
                    <LoadingButton
                        id="confirmar"
                        type="submit"
                        loading={isLoading}
                        disabled={isLoading}
                        color="primary"
                        variant="contained"
                        style={{ width: "159px", height: "44px" }}
                    >
                        Sim
                    </LoadingButton>
                </DialogActions>
            </form>
        </Dialog>
    );
}
